// chat/actions.ts — the chat control executor (cloned from Oriora Expense's src/chat/actions.ts).
// The client router (chat/brain.ts) maps ONE user message → ONE ChatAction; this file EXECUTES that
// action against the on-device store (src/store.ts). Everything here is LOCAL Tauri IPC (SQLite +
// the native folder picker) — no network, no billing — EXCEPT `ask`, which reuses the app's ALREADY
// LIVE BYOK endpoint (askCodebase in ../brain.ts) exactly the way AskBox does: local FTS5 retrieval
// first, then ONE billed call with only the matched snippets.
//
// HITL discipline: the destructive actions (delete_repo, clear_history) are NEVER executed from a
// chat turn on their own — the Chat UI holds them in `pending` and requires an explicit Confirm tap
// first. This executor just performs the action once the UI hands it back.

import { open as openFolderDialog } from "@tauri-apps/plugin-dialog";
import { store, snippetCite, type Repo, type Symbol, type HistoryEntry } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "knowcode";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { askCodebase, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// Compact rows rendered inline in a chat bubble.
export interface RepoRow {
  id: string;
  name: string;
  file_count: number;
  symbol_count: number;
}
export interface SymbolRow {
  id: string;
  name: string;
  kind: string;
  path: string;
  line: number;
}
export interface HistoryRow {
  id: string;
  question: string;
  repo_name: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "index_repo" } // opens the SAME native folder picker as the "Index a folder" button
  | { kind: "delete_repo"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "list_repos" }
  | { kind: "find_symbol"; repoId: string; repoName: string; query: string }
  | { kind: "ask"; repoId: string; repoName: string; question: string }
  | { kind: "show_history" }
  | { kind: "clear_history" } // UI confirms before this reaches executeAction
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string; // shown back in the chat
  changed?: boolean; // repos/history changed → the main view should refresh
  repoRows?: RepoRow[];
  symbolRows?: SymbolRow[];
  historyRows?: HistoryRow[];
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "index_repo": {
        const sel = await openFolderDialog({ directory: true, multiple: false, title: "Choose a project folder to index" });
        const picked = Array.isArray(sel) ? sel[0] ?? null : sel;
        if (!picked) return { ok: true, message: "Okay — didn't pick a folder." };
        const summary = await store.indexFolder(picked);
        const skipped = summary.skipped ? ` (${summary.skipped} skipped)` : "";
        return {
          ok: true,
          message: `Indexed "${summary.repo.name}" — ${summary.repo.file_count} files, ${summary.repo.symbol_count} symbols${skipped}.`,
          changed: true,
        };
      }

      case "delete_repo":
        await store.deleteRepo(a.id);
        return { ok: true, message: `Removed the index for ${a.label ?? "that repo"}. Your source files are untouched on disk.`, changed: true };

      case "list_repos": {
        const repos: Repo[] = await store.listRepos();
        const rows: RepoRow[] = repos.map((r) => ({ id: r.id, name: r.name, file_count: r.file_count, symbol_count: r.symbol_count }));
        return {
          ok: true,
          message: repos.length === 0 ? 'No repos indexed yet — try "index a folder".' : `${repos.length} repo${repos.length === 1 ? "" : "s"} indexed.`,
          repoRows: rows,
        };
      }

      case "find_symbol": {
        const hits: Symbol[] = await store.findSymbols(a.repoId, a.query, 20);
        const rows: SymbolRow[] = hits.map((s) => ({ id: s.id, name: s.name, kind: s.kind, path: s.path, line: s.line }));
        return {
          ok: true,
          message:
            hits.length === 0
              ? `No symbol matching "${a.query}" in ${a.repoName}.`
              : `${hits.length} symbol match${hits.length === 1 ? "" : "es"} in ${a.repoName}.`,
          symbolRows: rows,
        };
      }

      case "ask": {
        const hits = await store.searchCode(a.repoId, a.question, 10);
        if (hits.length === 0) {
          return { ok: true, message: "I couldn't find that in the indexed code. Try a function or file name, or re-index if the code changed." };
        }
        const r = await askCodebase(a.question, hits);
        if (!r.answer) return { ok: true, message: "I couldn't find that in the indexed code." };
        try {
          await store.addHistory(a.repoId, a.repoName, a.question, r.answer, r.citations.map((c) => snippetCite(c)));
        } catch {
          /* history is best-effort, never block the answer */
        }
        const cites = r.citations.slice(0, 5).map((c) => snippetCite(c)).join(" · ");
        return { ok: true, message: cites ? `${r.answer}\n\nSources: ${cites}` : r.answer };
      }

      case "show_history": {
        const items: HistoryEntry[] = await store.listHistory(20);
        const rows: HistoryRow[] = items.map((h) => ({ id: h.id, question: h.question, repo_name: h.repo_name }));
        return {
          ok: true,
          message: items.length === 0 ? "No questions asked yet." : `${items.length} recent question${items.length === 1 ? "" : "s"}.`,
          historyRows: rows,
        };
      }

      case "clear_history":
        await store.clearHistory();
        return { ok: true, message: "Cleared your question history.", changed: true };

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my repos" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole index). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    if (e instanceof BrainError) return { ok: false, message: e.message };
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
